import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../global/AuthContext";
import LevelBadge from "../../../global/LevelBadge";
import { formatBoardDate } from "../../../component/board/BoardDetail";
import { boardError, freeBoardApi, FreePostPage } from "./api";
import "../../../assets/css/container/board/freeboard/FreeBoard.css";

export default function FreeBoardList() {
    const [params, setParams] = useSearchParams();
    const requested = Number(params.get("page") || 1);
    const page = Number.isSafeInteger(requested) && requested > 0 && requested <= 2147483647 ? requested : 1;
    const { user } = useAuth();
    const [data, setData] = useState<FreePostPage>({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError("");
        freeBoardApi.list(page, controller.signal).then(({ data: next }) => {
            if (controller.signal.aborted) return;
            const last = Math.max(1, Math.ceil(next.total / 20));
            if (page > last) { setParams({ page: String(last) }, { replace: true }); return; }
            setData(next);
        }).catch(err => { if (!controller.signal.aborted) setError(boardError(err)); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [page, retry, setParams]);
    const maxPage = Math.max(1, Math.ceil(data.total / 20));
    const start = Math.floor((page - 1) / 10) * 10 + 1;
    const pages = Array.from({ length: Math.max(0, Math.min(10, maxPage - start + 1)) }, (_, i) => start + i);

    return <section className="FreeBoard">
        <header className="FreeBoardHeader"><h1>자유게시판</h1><Link className="FreeBoardButton" to={user ? "/freeboard/write" : "/login"}>{user ? "글쓰기" : "로그인 후 글쓰기"}</Link></header>
        <p className="FreeBoardCount">전체 {data.total.toLocaleString()}개</p>
        {error ? <div role="alert">{error} <button onClick={() => setRetry(n => n + 1)}>다시 시도</button></div> :
            <div className="FreeBoardScroll" tabIndex={0} role="region" aria-label="자유게시판 목록" aria-busy={loading}>
                <table className="FreeBoardTable">
                    <colgroup><col className="FreeNumber"/><col/><col className="FreeAuthor"/><col className="FreeDate"/><col className="FreeViews"/></colgroup>
                    <thead><tr><th scope="col">번호</th><th scope="col">제목</th><th scope="col">글쓴이</th><th scope="col">등록일</th><th scope="col">조회</th></tr></thead>
                    <tbody>{loading ? <tr><td colSpan={5}>불러오는 중...</td></tr> : data.items.length === 0 ? <tr><td colSpan={5}>아직 게시글이 없습니다.</td></tr> : data.items.map(post =>
                        <tr key={post.id}>
                            <td className="FreeNumberValue">{post.id.toLocaleString()}</td>
                            <td><Link title={post.title} to={`/freeboard/${post.id}?page=${page}`}>{post.title}</Link></td>
                            <td className="FreeAuthorValue" title={post.authorNickname}><LevelBadge level={post.authorLevel}/><span>{post.authorNickname}</span></td>
                            <td><time dateTime={post.createdAt}>{formatBoardDate(post.createdAt)}</time></td>
                            <td className="FreeViewValue">{post.viewCount.toLocaleString()}</td>
                        </tr>)}</tbody>
                </table>
            </div>}
        <nav className="FreePagination" aria-label="게시글 페이지">
            <button disabled={loading || page <= 1} onClick={() => setParams({ page: String(page - 1) })}>이전</button>
            {pages.map(p => <button key={p} aria-current={p === page ? "page" : undefined} disabled={loading} onClick={() => setParams({ page: String(p) })}>{p}</button>)}
            <button disabled={loading || page >= maxPage} onClick={() => setParams({ page: String(page + 1) })}>다음</button>
        </nav>
    </section>;
}
